import { ExportResult, ExportResultCode } from '@opentelemetry/core';
import { OtelRemoteConfigs } from '@/telemetry-sdk';
import { OTLPExporterNodeConfigBase } from '@opentelemetry/otlp-exporter-base';
import { diag } from '@opentelemetry/api';
import { OTLPLogExporter } from '@opentelemetry/exporter-logs-otlp-http';
import type { LogRecordExporter, ReadableLogRecord } from '@opentelemetry/sdk-logs';

const logger = diag.createComponentLogger({
  namespace: '✨ @telemetry-sdk/processors/dynamicOtelLoggingExporter',
});

export class DynamicOtelLoggingExporter implements LogRecordExporter {
  private exporter: OTLPLogExporter | null;
  private enabled: boolean;
  private loggingUrl: string | undefined;
  // Send {} for headers so that XHR is used to overcome the cors errors.
  private headers: Record<string, string> = {};

  constructor(url: string, isEnabled: boolean) {
    this.enabled = isEnabled;
    this.loggingUrl = url;
    this.exporter = null;
    if (isEnabled) {
      this.exporter = this.createLoggingExporter();
    }
  }

  updateConfigs(remoteConfigs: OtelRemoteConfigs): void {
    let loggingConfigsChanged: boolean =
      remoteConfigs.loggingEnabled != this.enabled || remoteConfigs.loggingCollectorURL != this.loggingUrl;
    if (!loggingConfigsChanged) {
      return;
    }

    logger.info(
      `Updating trace configs for DynamicLogsExporter enabled:${this.enabled} => ${remoteConfigs.loggingEnabled}, tracesUrl: ${this.loggingUrl} => ${remoteConfigs.loggingCollectorURL}`
    );

    this.enabled = remoteConfigs.loggingEnabled;
    this.loggingUrl = remoteConfigs.loggingCollectorURL;

    if (!this.enabled) {
      this.disable();
      return;
    }

    this.enable();
  }

  enable() {
    if (this.enabled) {
      return;
    }
    this.exporter = this.createLoggingExporter();
    this.enabled = true;
  }

  private createLoggingExporter() {
    const exporterOptions: OTLPExporterNodeConfigBase = {
      url: this.loggingUrl,
      headers: this.headers as Record<string, string> | undefined,
    };
    return new OTLPLogExporter(exporterOptions);
  }

  disable() {
    this.exporter = null;
    this.enabled = false;
  }

  /** Stops the exporter. */
  shutdown(): Promise<void> {
    return this.exporter ? this.exporter.shutdown() : Promise.resolve();
  }

  export(logs: ReadableLogRecord[], resultCallback: (result: ExportResult) => void): void {
    if (this.enabled && this.exporter) {
      this.exporter.export(logs, resultCallback);
    } else {
      // Drop logs if not enabled
      resultCallback({ code: ExportResultCode.SUCCESS });
    }
  }
}
